import React, { useState } from 'react';
import { 
  Shield, 
  Wrench, 
  Clock, 
  Phone, 
  Mail, 
  MapPin, 
  RefreshCw, 
  CheckCircle2, 
  AlertCircle 
} from 'lucide-react';

const Maintenance = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const currentYear = new Date().getFullYear();

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      window.location.reload();
    }, 600);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-950 via-gray-900 to-gray-950 text-white flex flex-col justify-between selection:bg-secondary-400 selection:text-gray-950">
      {/* Background ambient decorative glows */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/3 -right-40 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 left-1/3 w-96 h-96 bg-primary-700/15 rounded-full blur-3xl" />
      </div>

      {/* Top Header / Branding Bar */}
      <header className="relative z-10 w-full border-b border-white/10 bg-primary-950/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src="/images/logo/arham-secure-logo-white.png"
              alt="Arham Secure"
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </div>

          <div className="hidden sm:flex items-center space-x-2 bg-primary-900/60 border border-primary-500/30 px-3.5 py-1.5 rounded-full text-xs font-medium text-primary-200">
            <Shield className="w-3.5 h-3.5 text-secondary-400" />
            <span>IRDAI Licensed Direct Broker • License No. 1104</span>
          </div>

          <a
            href="tel:02240577700"
            className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200"
          >
            <Phone className="w-3.5 h-3.5 text-secondary-400" />
            <span>(022) 405-77700</span>
          </a>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-4xl w-full mx-auto text-center">
          
          {/* Status Badge */}
          <div className="inline-flex items-center space-x-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-300 px-4 py-2 rounded-full mb-8 text-xs sm:text-sm font-semibold tracking-wide uppercase">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
            </span>
            <span>Platform Under Scheduled Maintenance</span>
          </div>

          {/* Icon Presentation */}
          <div className="relative inline-flex items-center justify-center mb-8">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-tr from-primary-800 to-primary-600/80 border border-primary-400/30 shadow-2xl flex items-center justify-center transform -rotate-3 hover:rotate-0 transition-transform duration-300">
              <Shield className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-tr from-secondary-400 to-secondary-500 text-gray-950 flex items-center justify-center shadow-lg border-2 border-gray-950">
              <Wrench className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>

          {/* Heading and Summary */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 font-montserrat">
            We are currently <span className="bg-gradient-to-r from-secondary-400 to-primary-400 bg-clip-text text-transparent">under maintenance</span>
          </h1>

          <p className="text-gray-300 text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
            We are performing scheduled infrastructure upgrades to enhance performance, platform security, and our corporate insurance advisory services. We apologize for any temporary inconvenience.
          </p>

          {/* Assurance Highlights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left max-w-3xl mx-auto mb-10">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm hover:border-primary-500/40 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-primary-500/20 text-primary-300 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h2 className="text-sm font-bold text-white mb-1">Existing Policies Active</h2>
              <p className="text-xs text-gray-400 leading-relaxed">
                All existing policy covers and corporate insurance terms remain 100% active and protected without interruption.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm hover:border-primary-500/40 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center mb-3">
                <Clock className="w-5 h-5" />
              </div>
              <h2 className="text-sm font-bold text-white mb-1">Estimated Downtime</h2>
              <p className="text-xs text-gray-400 leading-relaxed">
                Our technical team is working actively to complete the upgrades. Services will be restored shortly.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm hover:border-primary-500/40 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-secondary-500/20 text-secondary-300 flex items-center justify-center mb-3">
                <AlertCircle className="w-5 h-5" />
              </div>
              <h2 className="text-sm font-bold text-white mb-1">Urgent Claims & Quotes</h2>
              <p className="text-xs text-gray-400 leading-relaxed">
                Need urgent claim assistance or a new policy quotation? Our executive support desk is fully reachable via phone and email.
              </p>
            </div>
          </div>

          {/* Contact & Support Cards */}
          <div className="bg-gradient-to-r from-primary-900/60 via-primary-950/80 to-primary-900/60 border border-primary-500/30 rounded-2xl p-6 sm:p-8 max-w-3xl mx-auto shadow-2xl backdrop-blur-md mb-8">
            <h2 className="text-lg sm:text-xl font-bold text-white mb-2 font-montserrat">
              Need immediate assistance? Contact our team directly
            </h2>
            <p className="text-xs sm:text-sm text-gray-300 mb-6">
              Our corporate insurance specialists are available to answer inquiries, guide claims, and issue immediate policy quotes.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href="tel:02240577700"
                className="flex items-center justify-center space-x-3 bg-secondary-400 hover:bg-secondary-500 text-gray-950 font-bold py-3.5 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-secondary-500/20"
              >
                <Phone className="w-5 h-5" />
                <span>Call (022) 405-77700</span>
              </a>

              <a
                href="mailto:info@arhamsecure.com"
                className="flex items-center justify-center space-x-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold py-3.5 px-6 rounded-xl transition-all duration-200"
              >
                <Mail className="w-5 h-5 text-secondary-400" />
                <span>info@arhamsecure.com</span>
              </a>
            </div>

            <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-2 text-xs text-gray-400">
              <div className="flex items-center space-x-1.5">
                <MapPin className="w-3.5 h-3.5 text-primary-400 shrink-0" />
                <span>Times Square, Andheri East, Mumbai 400 059</span>
              </div>
              <span className="hidden sm:inline text-gray-600">•</span>
              <span>Direct Broker (Composite) IRDAI Lic. No. 1104</span>
            </div>
          </div>

          {/* Refresh Action */}
          <div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center space-x-2 text-xs sm:text-sm text-gray-400 hover:text-white transition-colors duration-200 py-2 px-4 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-secondary-400' : ''}`} />
              <span>{isRefreshing ? 'Checking system status...' : 'Check again / Refresh page'}</span>
            </button>
          </div>

        </div>
      </main>

      {/* Subtle Footer */}
      <footer className="relative z-10 w-full border-t border-white/10 py-6 text-center text-xs text-gray-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p>© {currentYear} Arham Secure Insurance Brokers Pvt. Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Maintenance;
